// components/Navbar/Navbar.jsx
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Container,
  useMediaQuery,
  useTheme,
  alpha,
  Divider,
  Fade,
  Popper,
  Paper,
  Grow,
  ClickAwayListener,
  MenuList,
  MenuItem,
  Modal,
  Backdrop,
  TextField,
  Typography,
  Stack,
  Grid,
  InputAdornment,
  IconButton as MuiIconButton,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  Checkbox,
  Alert,
  CircularProgress,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import HomeIcon from '@mui/icons-material/Home';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import FactoryIcon from '@mui/icons-material/Factory';
import DiamondIcon from '@mui/icons-material/Diamond';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import InsightsIcon from '@mui/icons-material/Insights';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import WorkIcon from '@mui/icons-material/Work';
import MessageIcon from '@mui/icons-material/Message';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import VerifiedIcon from '@mui/icons-material/Verified';
import MobileDrawer from './MobileDrawer';
import LiveTicker from '../UI/LiveTicker';

const navItems = [
  { path: '/', label: 'Home', icon: <HomeIcon sx={{ fontSize: 16 }} /> },
  { path: '/mining', label: 'Mining', icon: <FactoryIcon sx={{ fontSize: 16 }} /> },
  { path: '/coin-minting', label: 'Minting', icon: <MonetizationOnIcon sx={{ fontSize: 16 }} /> },
  { path: '/metal-processing', label: 'Processing', icon: <PrecisionManufacturingIcon sx={{ fontSize: 16 }} /> },
  { path: '/distribution', label: 'Distribution', icon: <LocalShippingIcon sx={{ fontSize: 16 }} /> },
  { path: '/market-data', label: 'Market', icon: <ShowChartIcon sx={{ fontSize: 16 }} /> },
  { path: '/contact', label: 'Contact', icon: <ContactMailIcon sx={{ fontSize: 16 }} /> },
];

// EmailJS configuration (using free EmailJS service)
// Sign up at https://www.emailjs.com/ for free API keys
const EMAILJS_SERVICE_ID = 'service_pentillion'; // Replace with your EmailJS service ID
const EMAILJS_TEMPLATE_ID = 'template_private_office'; // Replace with your EmailJS template ID
const EMAILJS_PUBLIC_KEY = 'YOUR_PUBLIC_KEY'; // Replace with your EmailJS public key

// Private Office Form Component - Executive Only with HIGHEST Z-INDEX
const PrivateOfficeForm = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    linkedin: '',
    company: '',
    position: '',
    investmentRange: '',
    message: '',
    agreeTerms: false,
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const investmentRanges = [
    '$250,000 - $500,000',
    '$500,000 - $1,000,000',
    '$1,000,000 - $5,000,000',
    '$5,000,000 - $10,000,000',
    '$10,000,000+',
  ];

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const sendEmail = async () => {
    // Method 1: Using EmailJS (Recommended - Free tier available)
    // Uncomment this block and configure your EmailJS account
    /*
    try {
      const emailParams = {
        to_email: 'john.asfar@worldwidecorporation.com',
        from_name: formData.fullName,
        from_email: formData.email,
        phone: formData.phone,
        linkedin: formData.linkedin,
        company: formData.company,
        position: formData.position,
        investment_range: formData.investmentRange,
        message: formData.message,
        submitted_at: new Date().toLocaleString(),
      };

      const response = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          service_id: EMAILJS_SERVICE_ID,
          template_id: EMAILJS_TEMPLATE_ID,
          user_id: EMAILJS_PUBLIC_KEY,
          template_params: emailParams,
        }),
      });

      if (!response.ok) throw new Error('Email send failed');
      return true;
    } catch (error) {
      console.error('EmailJS error:', error);
      return false;
    }
    */

    // Method 2: Using a backend API endpoint (Create a simple API route)
    // Uncomment this block if you have a backend endpoint
    /*
    try {
      const response = await fetch('/api/send-private-office-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) throw new Error('Email send failed');
      return true;
    } catch (error) {
      console.error('API error:', error);
      return false;
    }
    */

    // Method 3: Using mailto: link with pre-filled content (Fallback)
    const subject = `Executive Access Request: ${formData.fullName} - ${formData.company}`;
    const body = `
Executive Office Application Details:

===========================================
PERSONAL INFORMATION
===========================================
Full Name: ${formData.fullName}
Email: ${formData.email}
Phone: ${formData.phone || 'Not provided'}
LinkedIn: ${formData.linkedin || 'Not provided'}

===========================================
CORPORATE INFORMATION
===========================================
Company: ${formData.company}
Position: ${formData.position || 'Not provided'}

===========================================
INVESTMENT DETAILS
===========================================
Target Investment Range: ${formData.investmentRange || 'Not specified'}

===========================================
MESSAGE / OBJECTIVES
===========================================
${formData.message || 'No message provided'}

===========================================
Submitted: ${new Date().toLocaleString()}
===========================================
    `;

    window.open(`mailto:john.asfar@worldwidecorporation.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
    return true;
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.fullName || !formData.email || !formData.company) {
      setError('Please fill in all required fields');
      return;
    }

    if (!formData.agreeTerms) {
      setError('Please agree to the terms and conditions');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Send email using the selected method
      const emailSent = await sendEmail();
      
      if (emailSent) {
        setSuccess(true);
        
        // Reset form after 2 seconds and close modal
        setTimeout(() => {
          setSuccess(false);
          onClose();
          setFormData({
            fullName: '',
            email: '',
            phone: '',
            linkedin: '',
            company: '',
            position: '',
            investmentRange: '',
            message: '',
            agreeTerms: false,
          });
        }, 2000);
      } else {
        setError('Unable to send application. Please try again or contact us directly.');
      }
    } catch (err) {
      console.error('Submission error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
        sx: {
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          zIndex: 9998,
        },
      }}
      sx={{
        zIndex: 9999, // Highest z-index to appear above everything
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: 550, md: 600 },
            maxHeight: '90vh',
            overflow: 'auto',
            bgcolor: '#0a0a2a',
            borderRadius: 4,
            boxShadow: '0 25px 50px rgba(0,0,0,0.5)',
            border: '1px solid rgba(212,175,55,0.4)',
            outline: 'none',
            background: 'linear-gradient(135deg, #0a0a2a 0%, #0d0d35 100%)',
            zIndex: 10000, // Ensure modal content is above backdrop
          }}
        >
          {/* Header */}
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 10001,
              background: 'linear-gradient(135deg, #0a0a2a 0%, #0d0d35 100%)',
              borderBottom: '1px solid rgba(212,175,55,0.3)',
              p: 3,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #D4AF37 0%, #B8960C 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <DiamondIcon sx={{ color: '#0a0a2a', fontSize: 22 }} />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#D4AF37' }}>
                    Executive Office
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: alpha('#FFFFFF', 0.6), maxWidth: 400 }}>
                  By invitation only — please complete this confidential form to begin the verification process
                </Typography>
              </Box>
              <IconButton
                onClick={onClose}
                sx={{
                  color: '#D4AF37',
                  '&:hover': {
                    bgcolor: alpha('#D4AF37', 0.1),
                    transform: 'scale(1.05)',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Form Body */}
          <Box sx={{ p: 3 }}>
            {success ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #D4AF37 0%, #B8960C 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 48, color: '#0a0a2a' }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#D4AF37', mb: 1 }}>
                  Application Received
                </Typography>
                <Typography variant="body2" sx={{ color: alpha('#FFFFFF', 0.6) }}>
                  A member of our executive team will contact you within 24 hours.
                </Typography>
              </Box>
            ) : (
              <>
                {error && (
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mb: 3, 
                      borderRadius: 2,
                      bgcolor: alpha('#f44336', 0.1),
                      color: '#ff6b6b',
                      '& .MuiAlert-icon': { color: '#ff6b6b' }
                    }}
                  >
                    {error}
                  </Alert>
                )}

                {/* Executive Badge */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    mb: 3,
                    p: 1,
                    bgcolor: alpha('#D4AF37', 0.08),
                    borderRadius: 3,
                    border: '1px solid rgba(212,175,55,0.2)',
                  }}
                >
                  <VerifiedIcon sx={{ color: '#D4AF37', fontSize: 16 }} />
                  <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600, letterSpacing: 0.5 }}>
                    EXECUTIVE LEVEL ACCESS
                  </Typography>
                  <VerifiedIcon sx={{ color: '#D4AF37', fontSize: 16 }} />
                </Box>

                <Grid container spacing={2.5}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Full Name *"
                      placeholder="Johnathan Alexander Sterling"
                      value={formData.fullName}
                      onChange={handleChange('fullName')}
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon sx={{ color: '#D4AF37', fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: alpha('#FFFFFF', 0.05),
                          color: '#FFFFFF',
                          '& fieldset': {
                            borderColor: alpha('#D4AF37', 0.3),
                          },
                          '&:hover fieldset': {
                            borderColor: '#D4AF37',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#D4AF37',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: alpha('#FFFFFF', 0.6),
                          '&.Mui-focused': {
                            color: '#D4AF37',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Corporate Email *"
                      type="email"
                      placeholder="j.sterling@company.com"
                      value={formData.email}
                      onChange={handleChange('email')}
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon sx={{ color: '#D4AF37', fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: alpha('#FFFFFF', 0.05),
                          color: '#FFFFFF',
                          '& fieldset': {
                            borderColor: alpha('#D4AF37', 0.3),
                          },
                          '&:hover fieldset': {
                            borderColor: '#D4AF37',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#D4AF37',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: alpha('#FFFFFF', 0.6),
                          '&.Mui-focused': {
                            color: '#D4AF37',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Direct Phone"
                      placeholder="+1 (555) 000-0000"
                      value={formData.phone}
                      onChange={handleChange('phone')}
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon sx={{ color: '#D4AF37', fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: alpha('#FFFFFF', 0.05),
                          color: '#FFFFFF',
                          '& fieldset': {
                            borderColor: alpha('#D4AF37', 0.3),
                          },
                          '&:hover fieldset': {
                            borderColor: '#D4AF37',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#D4AF37',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: alpha('#FFFFFF', 0.6),
                          '&.Mui-focused': {
                            color: '#D4AF37',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="LinkedIn Profile (Optional)"
                      placeholder="linkedin.com/in/jsterling"
                      value={formData.linkedin}
                      onChange={handleChange('linkedin')}
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LinkedInIcon sx={{ color: '#D4AF37', fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: alpha('#FFFFFF', 0.05),
                          color: '#FFFFFF',
                          '& fieldset': {
                            borderColor: alpha('#D4AF37', 0.3),
                          },
                          '&:hover fieldset': {
                            borderColor: '#D4AF37',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#D4AF37',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: alpha('#FFFFFF', 0.6),
                          '&.Mui-focused': {
                            color: '#D4AF37',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Company/Entity Name *"
                      placeholder="Sterling Capital Partners"
                      value={formData.company}
                      onChange={handleChange('company')}
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BusinessIcon sx={{ color: '#D4AF37', fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: alpha('#FFFFFF', 0.05),
                          color: '#FFFFFF',
                          '& fieldset': {
                            borderColor: alpha('#D4AF37', 0.3),
                          },
                          '&:hover fieldset': {
                            borderColor: '#D4AF37',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#D4AF37',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: alpha('#FFFFFF', 0.6),
                          '&.Mui-focused': {
                            color: '#D4AF37',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Position / Title"
                      placeholder="Managing Partner"
                      value={formData.position}
                      onChange={handleChange('position')}
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <WorkIcon sx={{ color: '#D4AF37', fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: alpha('#FFFFFF', 0.05),
                          color: '#FFFFFF',
                          '& fieldset': {
                            borderColor: alpha('#D4AF37', 0.3),
                          },
                          '&:hover fieldset': {
                            borderColor: '#D4AF37',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#D4AF37',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: alpha('#FFFFFF', 0.6),
                          '&.Mui-focused': {
                            color: '#D4AF37',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel sx={{ color: alpha('#FFFFFF', 0.6), '&.Mui-focused': { color: '#D4AF37' } }}>
                        Target Investment Range
                      </InputLabel>
                      <Select
                        value={formData.investmentRange}
                        onChange={handleChange('investmentRange')}
                        label="Target Investment Range"
                        sx={{
                          borderRadius: 2,
                          backgroundColor: alpha('#FFFFFF', 0.05),
                          color: '#FFFFFF',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: alpha('#D4AF37', 0.3),
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#D4AF37',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#D4AF37',
                          },
                          '& .MuiSvgIcon-root': {
                            color: '#D4AF37',
                          },
                        }}
                      >
                        {investmentRanges.map(range => (
                          <MenuItem key={range} value={range}>{range}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Investment Objectives / Message"
                      placeholder="Please describe your investment objectives and any specific requirements..."
                      value={formData.message}
                      onChange={handleChange('message')}
                      variant="outlined"
                      multiline
                      rows={3}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                            <MessageIcon sx={{ color: '#D4AF37', fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: alpha('#FFFFFF', 0.05),
                          color: '#FFFFFF',
                          '& fieldset': {
                            borderColor: alpha('#D4AF37', 0.3),
                          },
                          '&:hover fieldset': {
                            borderColor: '#D4AF37',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#D4AF37',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: alpha('#FFFFFF', 0.6),
                          '&.Mui-focused': {
                            color: '#D4AF37',
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    bgcolor: alpha('#D4AF37', 0.05),
                    borderRadius: 2,
                    border: '1px solid rgba(212,175,55,0.15)',
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.agreeTerms}
                        onChange={(e) => setFormData({ ...formData, agreeTerms: e.target.checked })}
                        sx={{
                          color: alpha('#D4AF37', 0.5),
                          '&.Mui-checked': {
                            color: '#D4AF37',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.6) }}>
                        I confirm that I am an accredited investor and agree to the{' '}
                        <Link to="/terms" style={{ color: '#D4AF37', textDecoration: 'none' }}>
                          Terms of Service
                        </Link>{' '}
                        and{' '}
                        <Link to="/privacy" style={{ color: '#D4AF37', textDecoration: 'none' }}>
                          Privacy Policy
                        </Link>
                      </Typography>
                    }
                  />
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading}
                  sx={{
                    mt: 3,
                    background: 'linear-gradient(135deg, #D4AF37 0%, #B8960C 100%)',
                    color: '#0a0a2a',
                    fontWeight: 700,
                    py: 1.2,
                    borderRadius: 2,
                    fontSize: '0.9rem',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #E5C158 0%, #D4AF37 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 20px rgba(212, 175, 55, 0.4)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {loading ? <CircularProgress size={24} sx={{ color: '#0a0a2a' }} /> : 'Submit Confidential Application'}
                  {!loading && <SendIcon sx={{ ml: 1, fontSize: 18 }} />}
                </Button>

                <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.3), display: 'block', textAlign: 'center', mt: 2 }}>
                  This information is protected by our confidentiality agreement.
                  A dedicated relationship manager will respond within 24 hours.
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [privateOfficeOpen, setPrivateOfficeOpen] = useState(false);
  const [privateModalOpen, setPrivateModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrivateOfficeClick = () => {
    setPrivateModalOpen(true);
    setPrivateOfficeOpen(false);
    setAnchorEl(null);
  };

  const handlePrivateOfficeOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setPrivateOfficeOpen(true);
  };

  const handlePrivateOfficeClose = () => {
    setPrivateOfficeOpen(false);
    setAnchorEl(null);
  };

  return (
    <>
      <AppBar 
        position="fixed" 
        elevation={0}
        sx={{ 
          bgcolor: scrolled 
            ? alpha('#000066', 0.98) 
            : '#000066',
          backdropFilter: scrolled ? 'blur(20px)' : 'none',
          borderBottom: scrolled 
            ? '1px solid rgba(212, 175, 55, 0.25)' 
            : '1px solid rgba(212, 175, 55, 0.15)',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: 1100,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(212, 175, 55, 0.6), #D4AF37, rgba(212, 175, 55, 0.6), transparent)',
            opacity: scrolled ? 0.8 : 0.5,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: '10%',
            right: '10%',
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(212, 175, 55, 0.3), transparent)',
            opacity: scrolled ? 0.6 : 0.3,
          },
        }}
      >
        <Container maxWidth="xl">
          <Toolbar 
            disableGutters 
            sx={{ 
              justifyContent: 'space-between',
              minHeight: { xs: 64, md: 68 },
              py: { xs: 0.5, md: 0.5 },
            }}
          >
            {/* Logo Section - Premium */}
            <Box 
              component={Link} 
              to="/" 
              sx={{ 
                textDecoration: 'none', 
                display: 'flex', 
                alignItems: 'center',
                position: 'relative',
                '&:hover img': {
                  transform: 'scale(1.03)',
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  right: 0,
                  height: '2px',
                  background: 'linear-gradient(90deg, #D4AF37, transparent)',
                  transform: 'scaleX(0)',
                  transition: 'transform 0.3s ease',
                },
                '&:hover::after': {
                  transform: 'scaleX(1)',
                },
              }}
            >
              <Box
                component="img"
                src="/images/pentillion_logo.png"
                alt="Pentillion Logo"
                sx={{
                  height: { xs: 50, sm: 58, md: 62 },
                  width: 'auto',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.25))',
                }}
              />
            </Box>

            {/* Desktop Navigation - Premium with smaller fonts */}
            {!isMobile && (
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                {navItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  
                  return (
                    <Button
                      key={item.path}
                      component={Link}
                      to={item.path}
                      startIcon={item.icon}
                      sx={{
                        color: isActive ? '#D4AF37' : alpha('#FFFFFF', 0.85),
                        fontWeight: isActive ? 700 : 500,
                        fontSize: '0.7rem',
                        textTransform: 'none',
                        letterSpacing: 0.3,
                        px: 1.2,
                        py: 0.8,
                        borderRadius: 1.5,
                        position: 'relative',
                        gap: 0.8,
                        minWidth: 'auto',
                        whiteSpace: 'nowrap',
                        '& .MuiButton-startIcon': {
                          marginRight: 0.5,
                          color: isActive ? '#D4AF37' : alpha('#FFFFFF', 0.6),
                          transition: 'all 0.2s ease',
                          '& svg': {
                            fontSize: 14,
                          },
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          bottom: 4,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: isActive ? '20px' : '0px',
                          height: '2px',
                          background: 'linear-gradient(90deg, #D4AF37, #FFD700)',
                          borderRadius: '2px',
                          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        },
                        '&:hover': {
                          color: '#D4AF37',
                          backgroundColor: alpha('#D4AF37', 0.08),
                          '& .MuiButton-startIcon': {
                            color: '#D4AF37',
                            transform: 'translateY(-1px)',
                          },
                          '&::before': {
                            width: '28px',
                          },
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                  );
                })}
                
                <Divider 
                  orientation="vertical" 
                  flexItem 
                  sx={{ 
                    mx: 1, 
                    bgcolor: alpha('#D4AF37', 0.3), 
                    height: 28,
                    borderRadius: 1,
                  }} 
                />
                
                {/* Premium Private Office Button with Dropdown */}
                <Button
                  aria-describedby="private-office-popper"
                  variant="contained"
                  size="small"
                  endIcon={<ArrowDropDownIcon sx={{ fontSize: 14 }} />}
                  onClick={handlePrivateOfficeOpen}
                  sx={{
                    background: 'linear-gradient(135deg, #D4AF37 0%, #B8960C 100%)',
                    color: '#000066',
                    fontWeight: 600,
                    fontSize: '0.65rem',
                    textTransform: 'none',
                    letterSpacing: 0.5,
                    px: 1.5,
                    py: 0.6,
                    borderRadius: 2,
                    boxShadow: '0 3px 8px rgba(212, 175, 55, 0.3)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #E5C158 0%, #D4AF37 100%)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 5px 12px rgba(212, 175, 55, 0.4)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <DiamondIcon sx={{ fontSize: 12, mr: 0.5 }} />
                  Private
                </Button>
                
                <Popper
                  id="private-office-popper"
                  open={privateOfficeOpen}
                  anchorEl={anchorEl}
                  placement="bottom-end"
                  transition
                  disablePortal
                  sx={{ zIndex: 1300 }}
                >
                  {({ TransitionProps }) => (
                    <Grow {...TransitionProps} timeout={200}>
                      <Paper
                        elevation={8}
                        sx={{
                          mt: 1,
                          minWidth: 240,
                          bgcolor: '#0a0a2a',
                          border: '1px solid rgba(212, 175, 55, 0.3)',
                          borderRadius: 2,
                          overflow: 'hidden',
                          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
                        }}
                      >
                        <ClickAwayListener onClickAway={handlePrivateOfficeClose}>
                          <MenuList>
                            <MenuItem
                              onClick={handlePrivateOfficeClick}
                              sx={{
                                color: '#FFFFFF',
                                py: 1.2,
                                px: 2,
                                gap: 1.5,
                                fontSize: '0.75rem',
                                '&:hover': {
                                  bgcolor: alpha('#D4AF37', 0.1),
                                  color: '#D4AF37',
                                },
                                transition: 'all 0.2s ease',
                              }}
                            >
                              <PersonIcon sx={{ fontSize: 18 }} />
                              Executive Access Request
                            </MenuItem>
                            <MenuItem
                              component={Link}
                              to="/private/login"
                              onClick={handlePrivateOfficeClose}
                              sx={{
                                color: '#FFFFFF',
                                py: 1.2,
                                px: 2,
                                gap: 1.5,
                                fontSize: '0.75rem',
                                '&:hover': {
                                  bgcolor: alpha('#D4AF37', 0.1),
                                  color: '#D4AF37',
                                },
                                transition: 'all 0.2s ease',
                              }}
                            >
                              <DashboardIcon sx={{ fontSize: 18 }} />
                              Existing Client Portal
                            </MenuItem>
                            <Divider sx={{ borderColor: alpha('#D4AF37', 0.2), my: 0.5 }} />
                            <MenuItem
                              onClick={handlePrivateOfficeClick}
                              sx={{
                                color: '#D4AF37',
                                py: 1.2,
                                px: 2,
                                gap: 1.5,
                                fontSize: '0.7rem',
                                fontStyle: 'italic',
                                '&:hover': {
                                  bgcolor: alpha('#D4AF37', 0.1),
                                },
                              }}
                            >
                              <DiamondIcon sx={{ fontSize: 16 }} />
                              Schedule Private Consultation
                            </MenuItem>
                          </MenuList>
                        </ClickAwayListener>
                      </Paper>
                    </Grow>
                  )}
                </Popper>
              </Box>
            )}

            {/* Mobile Menu Button - Premium */}
            {isMobile && (
              <IconButton 
                onClick={handleDrawerToggle}
                sx={{
                  color: '#D4AF37',
                  '&:hover': {
                    backgroundColor: alpha('#D4AF37', 0.15),
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  border: '1px solid rgba(212, 175, 55, 0.4)',
                  borderRadius: '10px',
                  padding: '6px',
                  width: 40,
                  height: 40,
                }}
              >
                {mobileOpen ? <CloseIcon sx={{ fontSize: 20 }} /> : <MenuIcon sx={{ fontSize: 20 }} />}
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Spacer */}
      <Toolbar sx={{ minHeight: { xs: 72, md: 76 } }} />

      {/* Live Ticker */}
      <LiveTicker />

      {/* Private Office Application Modal - HIGHEST Z-INDEX */}
      <PrivateOfficeForm open={privateModalOpen} onClose={() => setPrivateModalOpen(false)} />

      {/* Premium Scroll to Top Button */}
      <Fade in={showScrollTop}>
        <IconButton
          onClick={scrollToTop}
          sx={{
            position: 'fixed',
            bottom: 30,
            right: 30,
            zIndex: 1000,
            bgcolor: '#D4AF37',
            color: '#000066',
            width: 44,
            height: 44,
            '&:hover': {
              bgcolor: '#B8960C',
              transform: 'translateY(-4px) scale(1.05)',
              boxShadow: '0 8px 25px rgba(212, 175, 55, 0.5)',
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)',
          }}
        >
          <KeyboardArrowUpIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </Fade>

      <MobileDrawer open={mobileOpen} onClose={handleDrawerToggle} navItems={navItems} />

      <style>
        {`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(15px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes shimmer {
            0% {
              background-position: -200% 0;
            }
            100% {
              background-position: 200% 0;
            }
          }
        `}
      </style>
    </>
  );
};

export default Navbar;